import { useCallback, useEffect, useMemo, useState } from "react";

import EmptyState from "../components/EmptyState";
import FieldError from "../components/FieldError";
import LoadingState from "../components/LoadingState";
import PageTitle from "../components/PageTitle";
import { adminApi, buildQuery } from "../services/adminApi";
import { formatCurrency, getResults } from "../utils/formatters";

const emptyWeeklyMenu = {
  name: "",
  menu_type: "weekly",
  start_date: "",
  end_date: "",
  location: "",
  entry_items: [],
};

function normalizeDateForApi(value) {
  if (!value) {
    return null;
  }

  return String(value).slice(0, 10);
}

function validateWeeklyMenu(draft, action) {
  const fieldErrors = {};

  if (!draft.location) {
    fieldErrors.locations = "Choose a location.";
  }

  if (!draft.name.trim()) {
    fieldErrors.name = "Enter a menu name.";
  }

  if (!draft.start_date) {
    fieldErrors.start_date = "Enter a start date.";
  }

  if (!draft.end_date) {
    fieldErrors.end_date = "Enter an end date.";
  }

  if (draft.start_date && draft.end_date && draft.end_date < draft.start_date) {
    fieldErrors.end_date = "End date cannot be before start date.";
  }

  if (action === "publish" && !draft.entry_items.length) {
    fieldErrors.entry_items = "Add at least one item before publishing.";
  }

  return fieldErrors;
}

function buildEntryFromItem(item, displayOrder) {
  const locationItem = item.location_items?.[0] || {};
  const priceOverride = locationItem.price_override || "";

  return {
    menu_item: item.id,
    price_override: priceOverride,
    is_available: true,
    display_order: displayOrder,
    special_note: "",
    include_sold_out: false,
  };
}

function getLocationPrice(item) {
  const locationItem = item.location_items?.[0] || {};
  return locationItem.price_override || item.base_price;
}

function getItemAvailabilityLabel(item) {
  const locationItem = item.location_items?.[0] || {};

  if (locationItem.sold_out) {
    return "Sold out";
  }

  if (!locationItem.is_available) {
    return "Unavailable";
  }

  return "Available";
}

export default function WeeklyMenuPage({ filters }) {
  const [availableItems, setAvailableItems] = useState([]);
  const [draft, setDraft] = useState({
    ...emptyWeeklyMenu,
    location: filters.location || "",
  });
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [itemsError, setItemsError] = useState("");
  const [itemsLoading, setItemsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [locationsError, setLocationsError] = useState("");
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [menus, setMenus] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");

  const activeLocations = useMemo(() => {
    return locations.filter((location) => location.is_active);
  }, [locations]);
  const selectedLocation = activeLocations.find((location) => {
    return String(location.id) === String(draft.location);
  });
  const fieldsDisabled = !selectedLocation;
  const currentActiveMenu = menus.find((menu) => menu.status_label === "published");
  const previousMenus = menus.filter((menu) => menu.id !== currentActiveMenu?.id);

  const loadLocations = useCallback(async () => {
    setLocationsLoading(true);
    setLocationsError("");

    try {
      const payload = await adminApi("/locations/");
      setLocations(getResults(payload));
    } catch (requestError) {
      setLocationsError("Unable to load locations. Try again.");
      setError(requestError);
    } finally {
      setLocationsLoading(false);
    }
  }, []);

  const loadMenus = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const payload = await adminApi(
        `/menus/${buildQuery({
          menu_type: "weekly",
          location: draft.location,
        })}`,
      );
      setMenus(getResults(payload));
    } catch (requestError) {
      setError(requestError);
    } finally {
      setLoading(false);
    }
  }, [draft.location]);

  const loadAvailableItems = useCallback(async () => {
    if (!draft.location) {
      setAvailableItems([]);
      return;
    }

    setItemsLoading(true);
    setItemsError("");

    try {
      const payload = await adminApi(
        `/menu-items/${buildQuery({
          location: draft.location,
          is_active: true,
          is_archived: false,
          page_size: 100,
        })}`,
      );
      setAvailableItems(getResults(payload));
    } catch (requestError) {
      setItemsError("Unable to load menu items for this location.");
      setError(requestError);
    } finally {
      setItemsLoading(false);
    }
  }, [draft.location]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  useEffect(() => {
    loadMenus();
    loadAvailableItems();
  }, [loadMenus, loadAvailableItems]);

  const updateDraft = (changes) => {
    setDraft((currentDraft) => ({ ...currentDraft, ...changes }));
  };

  const selectLocation = (locationId) => {
    setDraft((currentDraft) => ({
      ...currentDraft,
      location: locationId,
      entry_items: [],
    }));
    setFieldErrors({});
  };

  const isItemSelected = (itemId) => {
    return draft.entry_items.some((entry) => entry.menu_item === itemId);
  };

  const toggleItem = (item, checked) => {
    if (checked) {
      updateDraft({
        entry_items: [
          ...draft.entry_items,
          buildEntryFromItem(item, draft.entry_items.length),
        ],
      });
      return;
    }

    updateDraft({
      entry_items: draft.entry_items.filter((entry) => entry.menu_item !== item.id),
    });
  };

  const updateEntry = (itemId, changes) => {
    updateDraft({
      entry_items: draft.entry_items.map((entry) => {
        if (entry.menu_item !== itemId) {
          return entry;
        }

        return { ...entry, ...changes };
      }),
    });
  };

  const saveWeeklyMenu = async (action) => {
    const nextFieldErrors = validateWeeklyMenu(draft, action);

    if (Object.keys(nextFieldErrors).length) {
      setFieldErrors(nextFieldErrors);
      setError(null);
      return;
    }

    setSubmitting(true);
    setError(null);
    setFieldErrors({});
    setSuccess("");

    const payload = {
      name: draft.name,
      menu_type: "weekly",
      start_date: normalizeDateForApi(draft.start_date),
      end_date: normalizeDateForApi(draft.end_date),
      locations: [Number(draft.location)],
      is_active: true,
      is_published: action === "publish",
      entry_items: draft.entry_items.map((entry) => ({
        ...entry,
        price_override: entry.price_override || null,
      })),
    };

    try {
      await adminApi("/menus/", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      setDraft({ ...emptyWeeklyMenu, location: draft.location });
      setSuccess(action === "publish" ? "Weekly menu published." : "Weekly menu saved as draft.");
      await loadMenus();
    } catch (requestError) {
      setError(requestError);
      setFieldErrors(requestError.fieldErrors || {});
    } finally {
      setSubmitting(false);
    }
  };

  const publishMenu = async (menuId) => {
    setError(null);

    try {
      await adminApi(`/menus/${menuId}/publish/`, { method: "POST" });
      setSuccess("Weekly menu published.");
      await loadMenus();
    } catch (requestError) {
      setError(requestError);
      setFieldErrors(requestError.fieldErrors || {});
    }
  };

  const copyMenu = async (menuId) => {
    setError(null);

    try {
      await adminApi(`/menus/${menuId}/copy/`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      setSuccess("Weekly menu copied.");
      await loadMenus();
    } catch (requestError) {
      setError(requestError);
    }
  };

  const noActiveLocations = !locationsLoading && !locationsError && !activeLocations.length;

  return (
    <main className="admin-page flex flex-column gap-5">
      <PageTitle eyebrow="Weekly Menu" title="Location Weekly Menus">
        Create one weekly menu per location, then add only items assigned to that location.
      </PageTitle>

      {error ? <div className="alert alert-error">{error.message}</div> : null}
      {success ? <div className="alert alert-success">{success}</div> : null}

      <section className="card admin-panel">
        <header className="card-header">
          <h2>Create Weekly Menu</h2>
        </header>
        <div className="card-body flex flex-column gap-4">
          <label className="form-group">
            <span className="form-label">Location *</span>
            <select
              disabled={locationsLoading || noActiveLocations}
              value={draft.location}
              onChange={(event) => selectLocation(event.target.value)}
            >
              <option value="">
                {locationsLoading ? "Loading locations" : "Choose location"}
              </option>
              {activeLocations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
            <FieldError message={fieldErrors.locations || locationsError} />
          </label>

          {noActiveLocations ? (
            <div className="alert alert-warning">
              No active locations are available. Create or activate a location first.
            </div>
          ) : null}

          {!selectedLocation && !noActiveLocations ? (
            <p className="muted-text">Choose a location to load available menu items.</p>
          ) : null}

          {selectedLocation ? (
            <div className="alert alert-light">
              Building weekly menu for <strong>{selectedLocation.name}</strong>.
            </div>
          ) : null}

          <div className="form-row">
            <label className="form-group">
              <span className="form-label">Menu name *</span>
              <input
                disabled={fieldsDisabled}
                value={draft.name}
                onChange={(event) => updateDraft({ name: event.target.value })}
              />
              <FieldError message={fieldErrors.name} />
            </label>
            <label className="form-group">
              <span className="form-label">Start date *</span>
              <input
                disabled={fieldsDisabled}
                type="date"
                value={draft.start_date}
                onChange={(event) => updateDraft({ start_date: event.target.value })}
              />
              <FieldError message={fieldErrors.start_date} />
            </label>
            <label className="form-group">
              <span className="form-label">End date *</span>
              <input
                disabled={fieldsDisabled}
                type="date"
                value={draft.end_date}
                onChange={(event) => updateDraft({ end_date: event.target.value })}
              />
              <FieldError message={fieldErrors.end_date} />
            </label>
          </div>

          <fieldset className="admin-fieldset" disabled={fieldsDisabled}>
            <legend>Menu items</legend>
            {itemsLoading ? <LoadingState /> : null}
            {itemsError ? <div className="alert alert-error">{itemsError}</div> : null}
            {!itemsLoading && selectedLocation && !availableItems.length ? (
              <EmptyState text="No menu items are assigned to this location yet." />
            ) : null}
            <div className="weekly-item-list">
              {availableItems.map((item) => {
                const selected = isItemSelected(item.id);
                const entry = draft.entry_items.find((currentEntry) => {
                  return currentEntry.menu_item === item.id;
                });
                const locationItem = item.location_items?.[0] || {};
                const soldOut = Boolean(locationItem.sold_out);
                const unavailable = !locationItem.is_available;

                return (
                  <article className="weekly-item-row" key={item.id}>
                    <label className="checkbox-line">
                      <input
                        type="checkbox"
                        checked={selected}
                        disabled={unavailable}
                        onChange={(event) => toggleItem(item, event.target.checked)}
                      />
                      <span>
                        <strong>{item.name}</strong> - {formatCurrency(getLocationPrice(item))}
                      </span>
                    </label>
                    <span className={soldOut || unavailable ? "badge badge-warning" : "badge badge-success"}>
                      {getItemAvailabilityLabel(item)}
                    </span>

                    {selected ? (
                      <div className="weekly-entry-controls">
                        {soldOut ? (
                          <label className="checkbox-line">
                            <input
                              type="checkbox"
                              checked={entry.include_sold_out}
                              onChange={(event) => {
                                updateEntry(item.id, {
                                  include_sold_out: event.target.checked,
                                });
                              }}
                            />
                            Include sold-out item intentionally
                          </label>
                        ) : null}
                        <label className="form-group">
                          <span className="form-label">Price override</span>
                          <input
                            inputMode="decimal"
                            value={entry.price_override || ""}
                            onChange={(event) => {
                              updateEntry(item.id, {
                                price_override: event.target.value,
                              });
                            }}
                          />
                        </label>
                        <label className="form-group">
                          <span className="form-label">Special note</span>
                          <input
                            value={entry.special_note || ""}
                            onChange={(event) => {
                              updateEntry(item.id, {
                                special_note: event.target.value,
                              });
                            }}
                          />
                        </label>
                      </div>
                    ) : null}
                  </article>
                );
              })}
            </div>
            <FieldError message={fieldErrors.entry_items} />
          </fieldset>

          <div className="flex gap-2">
            <button
              className="btn btn-secondary"
              onClick={() => saveWeeklyMenu("draft")}
              disabled={fieldsDisabled || submitting}
              type="button"
            >
              {submitting ? "Saving" : "Save Draft"}
            </button>
            <button
              className="btn btn-primary"
              onClick={() => saveWeeklyMenu("publish")}
              disabled={fieldsDisabled || submitting}
              type="button"
            >
              {submitting ? "Publishing" : "Publish"}
            </button>
          </div>
        </div>
      </section>

      <section className="card admin-panel">
        <header className="card-header">
          <h2>Weekly Menus</h2>
        </header>
        <div className="card-body mini-grid">
          {loading ? <LoadingState /> : null}
          {!loading && !menus.length ? (
            <EmptyState text="This location does not have a weekly menu yet." />
          ) : null}
          {currentActiveMenu ? (
            <article className="menu-card mini-card is-active-menu" key={currentActiveMenu.id}>
              <strong>{currentActiveMenu.name}</strong>
              <span>{currentActiveMenu.location_name}</span>
              <span>
                {currentActiveMenu.start_date} - {currentActiveMenu.end_date}
              </span>
              <span className="badge badge-success">Current active weekly menu</span>
              <span>{currentActiveMenu.entries?.length || 0} items</span>
            </article>
          ) : null}
          {previousMenus.map((menu) => (
            <article className="menu-card mini-card" key={menu.id}>
              <strong>{menu.name}</strong>
              <span>{menu.location_name}</span>
              <span>{menu.start_date} - {menu.end_date}</span>
              <span className={menu.is_published ? "badge badge-success" : "badge badge-warning"}>
                {menu.status_label}
              </span>
              <span>{menu.entries?.length || 0} items</span>
              <span>Published: {menu.published_at || "Not published"}</span>
              <div className="flex gap-2">
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => publishMenu(menu.id)}
                  type="button"
                >
                  Publish
                </button>
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => copyMenu(menu.id)}
                  type="button"
                >
                  Copy Previous Week
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}